import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { Copy, Plus, ArrowClockwise, Trash, ArrowsClockwise, ShieldCheck, ClockCounterClockwise } from "@phosphor-icons/react";

const DEFAULT_STORY_TYPES = [
  "news_link", "personal_story", "observation", "photo_story",
  "video", "conservation_story",
];

function copy(text) {
  try {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  } catch { toast.error("Copy failed"); }
}

function KeyModal({ apiKey, siteName, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
      data-testid="dist-key-modal"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden border border-black/15 shadow-2xl">
        <div className="bg-[#2A1F14] text-[#E4A15C] px-4 py-3 flex items-center gap-2">
          <ShieldCheck size={16} weight="fill" />
          <div className="flex-1 font-mono-tech text-[10px] tracking-[0.32em] uppercase">API key issued</div>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-sm text-[#241209]">
            Copy this key now — it is shown <b>once</b> and cannot be retrieved later.
            Share it securely with the <b>{siteName}</b> integration team.
          </div>
          <pre
            data-testid="dist-key-value"
            className="rounded-lg bg-black/90 text-[#E4A15C] px-3 py-3 text-xs break-all whitespace-pre-wrap font-mono"
          >{apiKey}</pre>
          <div className="flex justify-between">
            <button
              onClick={() => copy(apiKey)}
              data-testid="dist-key-copy"
              className="inline-flex items-center gap-1.5 rounded-full border border-black/15 bg-white px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
            >
              <Copy size={12} weight="bold" /> Copy key
            </button>
            <button
              onClick={onClose}
              data-testid="dist-key-close"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#B85F2A] text-white px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
            >
              I've stored it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LogModal({ siteId, siteName, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get(`/distribution/sites/${siteId}/access-log`);
        setRows(data.log || []);
      } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load log"); }
      finally { setLoading(false); }
    })();
  }, [siteId]);
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
      data-testid="dist-log-modal"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden border border-black/15 shadow-2xl flex flex-col">
        <div className="bg-[#2A1F14] text-[#E4A15C] px-4 py-3 flex items-center gap-2">
          <ClockCounterClockwise size={16} weight="fill" />
          <div className="flex-1 font-mono-tech text-[10px] tracking-[0.32em] uppercase">Access log · {siteName}</div>
          <button data-testid="dist-log-close" onClick={onClose} className="text-xs opacity-80 hover:opacity-100">CLOSE</button>
        </div>
        <div className="p-4 overflow-y-auto">
          {loading ? (
            <div className="text-xs text-[#3A3830]/70">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-xs text-[#3A3830]/70">No recent calls.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase text-[#3A3830]/60">
                <tr className="border-b border-black/10">
                  <th className="py-1 text-left">At</th>
                  <th className="text-left">Endpoint</th>
                  <th className="text-right">Count</th>
                  <th className="text-left">IP</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-black/5">
                    <td className="py-1 whitespace-nowrap">{r.at?.slice(0, 19).replace("T", " ")}</td>
                    <td className="font-mono">{r.endpoint}</td>
                    <td className="text-right">{r.count}</td>
                    <td className="font-mono opacity-70">{r.ip || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateSiteForm({ onCreated }) {
  const [name, setName] = useState("");
  const [origin, setOrigin] = useState("");
  const [description, setDescription] = useState("");
  const [categoriesFilter, setCategoriesFilter] = useState("");
  const [maxPageSize, setMaxPageSize] = useState(50);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (name.trim().length < 2) { toast.error("Site name required"); return; }
    setBusy(true);
    try {
      const cats = categoriesFilter.split(",").map(s => s.trim()).filter(Boolean);
      const { data } = await http.post("/distribution/sites", {
        name: name.trim(),
        origin: origin.trim() || undefined,
        description: description.trim() || undefined,
        capabilities: {
          categories_filter: cats,
          max_page_size: parseInt(maxPageSize, 10) || 50,
          story_types_filter: DEFAULT_STORY_TYPES,
        },
      });
      toast.success(`Registered ${data.site.name}`);
      setName(""); setOrigin(""); setDescription(""); setCategoriesFilter("");
      onCreated?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-black/10 bg-white p-4 space-y-3" data-testid="dist-create-form">
      <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]/70">Register new partner site</div>
      <div className="grid md:grid-cols-2 gap-3">
        <input data-testid="dist-input-name" value={name} onChange={(e)=>setName(e.target.value)} placeholder="Name (e.g. Kerala.com)" className="rounded-md border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#B85F2A]" />
        <input data-testid="dist-input-origin" value={origin} onChange={(e)=>setOrigin(e.target.value)} placeholder="Origin URL (e.g. https://kerala.com)" className="rounded-md border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#B85F2A]" />
        <input data-testid="dist-input-categories" value={categoriesFilter} onChange={(e)=>setCategoriesFilter(e.target.value)} placeholder="Category filter (comma-sep, blank=all)" className="rounded-md border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#B85F2A]" />
        <input data-testid="dist-input-max-page" type="number" min="1" max="200" value={maxPageSize} onChange={(e)=>setMaxPageSize(e.target.value)} placeholder="Max page size" className="rounded-md border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#B85F2A]" />
      </div>
      <textarea data-testid="dist-input-description" value={description} onChange={(e)=>setDescription(e.target.value)} placeholder="Short description of what this site consumes…" rows={2} className="w-full rounded-md border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#B85F2A]" />
      <div className="flex justify-end">
        <button data-testid="dist-create-submit" onClick={submit} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full bg-[#B85F2A] text-white px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90 disabled:opacity-50">
          <Plus size={12} weight="bold" /> {busy ? "Creating…" : "Register site"}
        </button>
      </div>
    </div>
  );
}

export default function DistributionTab() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [issuedKey, setIssuedKey] = useState(null);
  const [logSite, setLogSite] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/distribution/sites");
      setSites(data.sites || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to list sites"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rotate = async (site) => {
    if (!window.confirm(`Rotate API key for "${site.name}"? The current key will stop working immediately.`)) return;
    try {
      const { data } = await http.post(`/distribution/sites/${site.site_id}/rotate-key`);
      setIssuedKey({ apiKey: data.api_key, siteName: site.name });
    } catch (e) { toast.error(e?.response?.data?.detail || "Rotation failed"); }
  };

  const toggleActive = async (site) => {
    if (site.active) {
      if (!window.confirm(`Deactivate "${site.name}"? Its API key will stop working immediately for all partner endpoints.`)) return;
    }
    try {
      if (site.active) {
        await http.delete(`/distribution/sites/${site.site_id}`);
      } else {
        await http.patch(`/distribution/sites/${site.site_id}`, { active: true });
      }
      toast.success(`${site.name} is now ${site.active ? "inactive" : "active"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-distribution-panel">
      <CreateSiteForm onCreated={(res) => {
        setIssuedKey({ apiKey: res.api_key, siteName: res.site.name });
        load();
      }} />

      <div className="rounded-xl border border-black/10 bg-white p-4">
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]/70 mb-3">
          Registered partner sites ({sites.length})
        </div>
        {loading ? (
          <div className="text-xs text-[#3A3830]/70">Loading…</div>
        ) : sites.length === 0 ? (
          <div className="text-xs text-[#3A3830]/70">No sites yet. Register one above to hand out an API key.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="dist-sites-table">
              <thead className="text-[10px] uppercase text-[#3A3830]/60">
                <tr className="border-b border-black/10">
                  <th className="py-2 text-left">Site</th>
                  <th className="text-left">Origin</th>
                  <th className="text-left">Scope</th>
                  <th className="text-right">Max/page</th>
                  <th className="text-center">Active</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sites.map((s) => (
                  <tr key={s.site_id} className="border-b border-black/5 align-top" data-testid={`dist-site-${s.site_id}`}>
                    <td className="py-2">
                      <div className="font-medium">{s.name}</div>
                      <div className="text-[10px] font-mono text-[#3A3830]/60">{s.site_id}</div>
                    </td>
                    <td className="text-xs opacity-80">{s.origin || "—"}</td>
                    <td className="text-xs">
                      {(s.capabilities?.categories_filter?.length
                        ? s.capabilities.categories_filter.join(", ")
                        : "all categories")}
                    </td>
                    <td className="text-right text-xs">{s.capabilities?.max_page_size || 50}</td>
                    <td className="text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-mono uppercase ${
                        s.active ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-700"
                      }`}>{s.active ? "yes" : "no"}</span>
                    </td>
                    <td className="text-right">
                      <div className="inline-flex gap-1">
                        <button data-testid={`dist-log-${s.site_id}`} onClick={() => setLogSite(s)} title="View access log" className="rounded-full border border-black/15 px-2 py-1 text-[10px] hover:bg-black/5">
                          <ClockCounterClockwise size={12} weight="bold" />
                        </button>
                        <button data-testid={`dist-rotate-${s.site_id}`} onClick={() => rotate(s)} title="Rotate API key" className="rounded-full border border-black/15 px-2 py-1 text-[10px] hover:bg-black/5">
                          <ArrowClockwise size={12} weight="bold" />
                        </button>
                        <button data-testid={`dist-toggle-${s.site_id}`} onClick={() => toggleActive(s)} title={s.active ? "Deactivate" : "Reactivate"} className={`rounded-full border px-2 py-1 text-[10px] ${
                          s.active ? "border-red-300 text-red-700 hover:bg-red-50" : "border-green-300 text-green-700 hover:bg-green-50"
                        }`}>
                          {s.active ? <Trash size={12} weight="bold" /> : <ArrowsClockwise size={12} weight="bold" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {issuedKey && (
        <KeyModal apiKey={issuedKey.apiKey} siteName={issuedKey.siteName} onClose={() => setIssuedKey(null)} />
      )}
      {logSite && (
        <LogModal siteId={logSite.site_id} siteName={logSite.name} onClose={() => setLogSite(null)} />
      )}
    </div>
  );
}
